import React, {useContext, useEffect, useId, useMemo, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faUsers} from "@fortawesome/free-solid-svg-icons";
import Badge from "../Badge.jsx";
import Button from "../Button.jsx";
import {FilterContext, normalizeCustomerWholesaleSelection} from "./DashboardFilterContextProvider.jsx";

export function getCustomerWholesaleOptions(t) {
    return [
        {value: null, label: t('filter.customer-wholesale.all')},
        {value: 'non_wholesale', label: t('filter.customer-wholesale.non-wholesale')},
        {value: 'wholesale', label: t('filter.customer-wholesale.wholesale')},
    ];
}

export function getCustomerWholesaleOptionClassName(selected) {
    const base = 'flex w-full items-center justify-between whitespace-nowrap rounded-md px-3 py-2 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400';
    return selected
        ? `${base} bg-violet-100 font-semibold text-violet-900 hover:bg-violet-100 focus:bg-violet-100 dark:bg-violet-500/30 dark:text-violet-100 dark:hover:bg-violet-500/40 dark:focus:bg-violet-500/40`
        : `${base} text-slate-800 hover:bg-slate-100 focus:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-700 dark:focus:bg-slate-700`;
}

export default function OrderCustomerWholesaleFilter() {
    const {t} = useTranslation();
    const {customerClassificationAvailable, customerWholesale, setCustomerWholesale} = useContext(FilterContext) ?? {};
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const menuId = useId();
    const helpId = useId();

    const normalizedCustomerWholesale = normalizeCustomerWholesaleSelection(customerWholesale);
    const options = useMemo(() => getCustomerWholesaleOptions(t), [t]);
    const selectedBadgeLabel = normalizedCustomerWholesale === 'wholesale' ? 'B2B' : 'B2C';

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (!wrapperRef.current?.contains(event.target)) {
                setOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
                triggerRef.current?.focus();
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    useEffect(() => {
        if (open) {
            (wrapperRef.current?.querySelector('[role="menuitemradio"][aria-checked="true"]')
                ?? wrapperRef.current?.querySelector('[role="menuitemradio"]'))?.focus();
        }
    }, [open]);

    const closeMenu = () => {
        setOpen(false);
        triggerRef.current?.focus();
    };

    const handleMenuItemKeyDown = (event) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            return;
        }

        event.preventDefault();
        const items = [...(wrapperRef.current?.querySelectorAll('[role="menuitemradio"]') ?? [])];
        const currentIndex = items.indexOf(event.currentTarget);
        const nextIndex = event.key === 'Home'
            ? 0
            : event.key === 'End'
                ? items.length - 1
                : (currentIndex + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items[nextIndex]?.focus();
    };

    if (!customerClassificationAvailable) {
        return null;
    }

    return <div
        ref={wrapperRef}
        className="filter-dropdown-trigger relative inline-flex"
        onBlur={(event) => {
            if (!wrapperRef.current?.contains(event.relatedTarget)) {
                setOpen(false);
            }
        }}
    >
        <Button
            ref={triggerRef}
            variant="outline-black"
            className="flex items-center justify-center gap-2 text-nowrap"
            title={t('filter.customer-wholesale.help')}
            aria-label={t('filter.customer-wholesale.title')}
            aria-describedby={helpId}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => setOpen((current) => !current)}
            onKeyDown={(event) => {
                if (!open && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    setOpen(true);
                }
            }}
        >
            <FontAwesomeIcon icon={faUsers} aria-hidden="true"/>
            {t('filter.customer-wholesale.title')}
            {normalizedCustomerWholesale && <Badge color="violet">{selectedBadgeLabel}</Badge>}
        </Button>
        <span id={helpId} className="sr-only">{t('filter.customer-wholesale.help')}</span>
        {open && <div
            id={menuId}
            role="menu"
            aria-label={t('filter.customer-wholesale.title')}
            className="absolute right-0 top-full z-50 mt-1.5 w-max min-w-56 max-w-[calc(100vw-2rem)] rounded-xl border border-[#b6bdc8] bg-white p-1.5 shadow-lg dark:border-gray-500 dark:bg-slate-800"
        >
            {options.map((option) => {
                const selected = option.value === normalizedCustomerWholesale;
                return <button
                    key={option.value ?? 'all'}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    tabIndex={selected ? 0 : -1}
                    className={getCustomerWholesaleOptionClassName(selected)}
                    onKeyDown={handleMenuItemKeyDown}
                    onClick={() => {
                        setCustomerWholesale(option.value);
                        closeMenu();
                    }}
                >
                    {option.label}
                    {selected && <FontAwesomeIcon icon={faCheck} aria-hidden="true" className="ml-3 text-violet-700 dark:text-violet-200"/>}
                </button>;
            })}
        </div>}
    </div>;
}
